import type { Dayjs } from 'dayjs';
import { dayjs } from 'utils/dayjs';
import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { serviceSchedule } from '../services/service';
import type { ShiftSegmentForm, UseFormAddShiftProps } from '../types/hooks';
import { usePermisoSchedule } from '../../../../auth/permisosModulos';

const initialSegment = (): ShiftSegmentForm => ({
    id: Date.now(),
    start: dayjs().startOf('day'),
    end: dayjs().startOf('day').add(1, 'hour'),
    type: 'REGULAR',
});

const minutesOfDay = (value: Dayjs) => value.hour() * 60 + value.minute();

const validateSegments = (segments: ShiftSegmentForm[]) => {
    const invalidSegmentIds = new Set<number>();
    const firstStart = segments[0]?.start;

    if (!firstStart?.isValid()) {
        return { invalidSegmentIds, isValid: false };
    }

    const dayBoundary = minutesOfDay(firstStart);
    const intervals = segments.flatMap((segment) => {
        if (!segment.start?.isValid() || !segment.end?.isValid()) {
            invalidSegmentIds.add(segment.id);
            return [];
        }

        let start = minutesOfDay(segment.start);
        if (start < dayBoundary) start += 24 * 60;

        let end = minutesOfDay(segment.end);
        while (end <= start) end += 24 * 60;

        if (end - start >= 24 * 60 || end > dayBoundary + 24 * 60) {
            invalidSegmentIds.add(segment.id);
        }

        return [{ id: segment.id, start, end }];
    });

    for (let index = 0; index < intervals.length; index += 1) {
        for (let nextIndex = index + 1; nextIndex < intervals.length; nextIndex += 1) {
            const current = intervals[index];
            const next = intervals[nextIndex];
            if (current.start < next.end && next.start < current.end) {
                invalidSegmentIds.add(current.id);
                invalidSegmentIds.add(next.id);
            }
        }
    }

    return {
        invalidSegmentIds,
        isValid: segments.length > 0 && invalidSegmentIds.size === 0,
    };
};

const useFormAddShift = ({ employees, selectedEmployeeIds = new Set<string>(), reload, option, setOption }: UseFormAddShiftProps) => {
    const { t } = useTranslation('schedule.form.add_shift');
    const { showAlert } = useRbSystemAlert();
    const canAddShift = usePermisoSchedule('submit_create_shift');

    const getSelectedEmployeeIds = () => {
        const ids = selectedEmployeeIds instanceof Set ? [...selectedEmployeeIds] : Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds : [];

        return [...new Set(ids.map(String))];
    };

    const [openForm, setOpenForm] = useState(false);
    const [formState, setFormState] = useState<{
        employeeIds: string[];
        date: Dayjs | null;
        repeat: number;
        segments: ShiftSegmentForm[];
    }>({
        employeeIds: [],
        date: dayjs(),
        repeat: 1,
        segments: [initialSegment()],
    });
    const [loading, setLoading] = useState(false);
    const [searchEmployee, setSearchEmployee] = useState('');
    const [validationMessage, setValidationMessage] = useState('');

    const { employeeIds, date, repeat, segments } = formState;
    const segmentValidation = validateSegments(segments);

    useEffect(() => {
        if (option === 'add_shift') {
            const preselectedEmployeeIds = getSelectedEmployeeIds();

            setFormState((previousState) => ({
                ...previousState,
                employeeIds: preselectedEmployeeIds.length > 0 ? preselectedEmployeeIds : previousState.employeeIds,
            }));

            if (canAddShift) setOpenForm(true);
            setOption(null);
        }
    }, [option, setOption, canAddShift, selectedEmployeeIds]);

    const setSafeDate: Dispatch<SetStateAction<Dayjs | null>> = (value) => {
        setFormState((previousState) => {
            const nextDate = typeof value === 'function' ? value(previousState.date) : value;

            return {
                ...previousState,
                date: nextDate ?? dayjs(),
            };
        });
    };

    const setEmployeeIds: Dispatch<SetStateAction<string[]>> = (value) => {
        setFormState((previousState) => ({
            ...previousState,
            employeeIds: typeof value === 'function' ? value(previousState.employeeIds).map(String) : value.map(String),
        }));
    };

    const setRepeat: Dispatch<SetStateAction<number>> = (value) => {
        setFormState((previousState) => ({
            ...previousState,
            repeat: typeof value === 'function' ? value(previousState.repeat) : value,
        }));
    };

    const resetForm = () => {
        setFormState({
            employeeIds: [],
            date: dayjs(),
            repeat: 1,
            segments: [initialSegment()],
        });
        setSearchEmployee('');
        setValidationMessage('');
    };

    const closeForm = () => {
        setOpenForm(false);
        resetForm();
    };

    const addSegment = () => {
        setFormState((previousState) => ({
            ...previousState,
            segments: [
                ...previousState.segments,
                (() => {
                    const previousEnd = previousState.segments.at(-1)?.end ?? dayjs().startOf('day');
                    const nextStart = previousEnd.add(1, 'hour');

                    return {
                        id: Date.now(),
                        start: nextStart,
                        end: nextStart.add(1, 'hour'),
                        type: 'REGULAR' as const,
                    };
                })(),
            ],
        }));
    };

    const updateSegment = (segmentId: number, changes: Partial<Omit<ShiftSegmentForm, 'id'>>) => {
        setFormState((previousState) => ({
            ...previousState,
            segments: previousState.segments.map((segment) => (segment.id === segmentId ? { ...segment, ...changes } : segment)),
        }));
    };

    const removeSegment = (segmentId: number) => {
        setFormState((previousState) => ({
            ...previousState,
            segments: previousState.segments.filter((segment) => segment.id !== segmentId),
        }));
    };

    const submit = async () => {
        if (!canAddShift) return;

        const parsedRepeat = repeat;

        if (employeeIds.length === 0 || !date?.isValid()) {
            setValidationMessage(t('add_shift.alerts.validation.required'));
            return;
        }

        const selectedEmployees = employeeIds.map((employeeId) => employees.find((employee) => String(employee.value) === String(employeeId)));

        if (selectedEmployees.some((employee) => !employee?.timezone)) {
            setValidationMessage(t('add_shift.alerts.validation.employee_timezone'));
            return;
        }

        if (!Number.isInteger(parsedRepeat) || parsedRepeat < 1) {
            setValidationMessage(t('add_shift.alerts.validation.repeat'));
            return;
        }

        if (!segmentValidation.isValid) {
            setValidationMessage(t('add_shift.alerts.validation.segments'));
            return;
        }

        const firstSegment = segments[0];
        const lastSegment = segments.at(-1)!;
        setLoading(true);

        try {
            const result = await serviceSchedule.addShift({
                body: {
                    employees: selectedEmployees.map((employee) => ({
                        employee_id: Number(employee!.value),
                        timezone: employee!.timezone,
                    })),
                    schedule: {
                        date: date.format('YYYY-MM-DD'),
                        start_time: firstSegment.start!.format('HH:mm:ss'),
                        end_time: lastSegment.end!.format('HH:mm:ss'),
                        override: false,
                        scheduler_create: true,
                        enable: true,
                        segments: segments.map((segment) => ({
                            type: segment.type,
                            start_time: segment.start!.format('HH:mm:ss'),
                            end_time: segment.end!.format('HH:mm:ss'),
                        })),
                    },
                    repeat: parsedRepeat,
                    client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                alert: { t, showAlert },
            });

            if (result?.total_success) {
                await reload();
                showAlert(
                    result.total_errors ? 'warning' : 'success',
                    t(result.total_errors ? 'add_shift.alerts.add_partial.message' : 'add_shift.alerts.add_success.message', {
                        success: result.total_success,
                        errors: result.total_errors,
                    }),
                );
                closeForm();
            } else if (result) {
                setValidationMessage(
                    t('add_shift.alerts.add_failed.message', {
                        errors: result.total_errors,
                    }),
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        t,
        title: t('add_shift.title'),
        openForm,
        setOpenForm,
        closeForm,
        employees,
        employeeIds,
        setEmployeeIds,
        date,
        setDate: setSafeDate,
        repeat,
        setRepeat,
        segments,
        addSegment,
        updateSegment,
        removeSegment,
        searchEmployee,
        setSearchEmployee,
        loading,
        segmentValidation,
        canSubmit: segmentValidation.isValid && !loading,
        validationMessage,
        setValidationMessage,
        submit,
    };
};

export { useFormAddShift };
